import { useEffect, useState, useMemo, useRef } from "react";
import {
  Dumbbell,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronDown,
  Calendar as CalendarIcon,
  Play,
  Square,
  ExternalLink,
  AlertCircle,
  MinusCircle,
  Shuffle,
  Loader2,
  Trophy,
  Weight,
  Route,
  TrendingUp,
} from "lucide-react";
import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { cn } from "../../lib/utils";
import api from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CLS = {
  completed: {
    text: "text-green-500",
    bg: "bg-green-500",
    light: "bg-green-500/10",
    icon: CheckCircle2,
    label: "Done",
  },
  missed: {
    text: "text-red-500",
    bg: "bg-red-500",
    light: "bg-red-500/10",
    icon: XCircle,
    label: "Missed",
  },
  upcoming: {
    text: "text-amber-400",
    bg: "bg-amber-400",
    light: "bg-amber-400/10",
    icon: Clock,
    label: "Upcoming",
  },
  in_progress: {
    text: "text-amber-400",
    bg: "bg-amber-400",
    light: "bg-amber-400/10",
    icon: Clock,
    label: "In Progress",
  },
  skipped: {
    text: "text-muted-foreground/60",
    bg: "bg-muted-foreground/30",
    light: "bg-muted-foreground/10",
    icon: MinusCircle,
    label: "Skipped",
  },
};

function parseLocalDate(str) {
  if (!str) return null;
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export default function DayDetailInline({
  session,
  allowMemberToComplete,
  onSuccess,
  hideHeader = false,
  enableTimer = false,
  isStaff = false,
  isLocked = false,
  isFlexible = false,
  workoutCard = {},
  missedWorkoutPolicy = "skip_missed_workout",
  missedCount = 0,
  fitnessPreparationDays = 0,
  fitnessRePreparationDays = 0,
}) {
  const isCompletionAllowed = allowMemberToComplete && !isLocked;
  const todayStr = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [localSession, setLocalSession] = useState(session);
  const [expandedSections, setExpandedSections] = useState({});
  const [expandedWorkoutId, setExpandedWorkoutId] = useState(null);
  const [activeTimer, setActiveTimer] = useState(null); // { secIdx, wIdx, setIdx }
  const [elapsed, setElapsed] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, description, onConfirm }
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);

  const isSessionMissed = useMemo(() => {
    return !!(
      localSession &&
      localSession.isUnresolved &&
      localSession.phase !== "rest"
    );
  }, [localSession]);
  const [previewDialog, setPreviewDialog] = useState({
    open: false,
    src: "",
    name: "",
  });
  const [showFlexibleDialog, setShowFlexibleDialog] = useState(false);
  const [tempSelected, setTempSelected] = useState("");

  const flexibleOptions = useMemo(() => {
    const card = workoutCard || {};
    const options = [];

    const getMergedSections = (phase, dayKey) => {
      const dayPlan =
        card[
        phase === "fitness_preparation"
          ? "fitnessPreparation"
          : phase === "fitness_re_preparation"
            ? "fitnessRePreparation"
            : phase === "rest"
              ? "rest"
              : "active"
        ]?.[dayKey] || {};

      let commonAll = card.common || {};
      let commonPhase = {};
      let commonPostPhase = {};

      if (phase === "fitness_preparation") {
        commonPhase = card.fitnessPreparationCommon || {};
        commonPostPhase = card.fitnessPreparationCommonPost || {};
      } else if (phase === "fitness_re_preparation") {
        commonPhase = card.fitnessRePreparationCommon || {};
        commonPostPhase = card.fitnessRePreparationCommonPost || {};
      } else if (phase === "rest") {
        commonPhase = card.restCommon || {};
        commonPostPhase = card.restCooldown || card.restCommonPost || {};
      } else {
        commonPhase = card.activeCommon || {};
        commonPostPhase = card.activeCommonPost || {};
      }

      const merged = [];
      const addSecs = (secGroup, label) => {
        if (secGroup && Array.isArray(secGroup.sections)) {
          secGroup.sections.forEach((sec) => {
            merged.push({
              name: sec.name,
              workouts: sec.workouts || [],
              label: label,
            });
          });
        }
      };

      addSecs(commonAll, "Global");
      addSecs(commonPhase, "Pre");
      addSecs(dayPlan, "Day");
      addSecs(commonPostPhase, "Post");

      return merged;
    };

    const getDayLabel = (phase, dayKey, defaultName) => {
      const dayPlan =
        card[
        phase === "fitness_preparation"
          ? "fitnessPreparation"
          : phase === "fitness_re_preparation"
            ? "fitnessRePreparation"
            : phase === "rest"
              ? "rest"
              : "active"
        ]?.[dayKey] || {};

      if (dayPlan.label && dayPlan.label.trim()) {
        return `${defaultName} (${dayPlan.label})`;
      }

      if (dayPlan.sections && Array.isArray(dayPlan.sections)) {
        const customNames = dayPlan.sections
          .map((s) => s.name)
          .filter(
            (name) => name && name.trim() && name.toLowerCase() !== "workout",
          );
        if (customNames.length > 0) {
          return `${defaultName} (${customNames.join(", ")})`;
        }
      }

      return defaultName;
    };

    // Preparation days
    const prepCount =
      fitnessPreparationDays > 0
        ? fitnessPreparationDays
        : card.fitnessPreparation
          ? Object.keys(card.fitnessPreparation).length
          : 0;

    for (let i = 1; i <= prepCount; i++) {
      const dayKey = `day-${i}`;
      options.push({
        value: `fitness_preparation:${dayKey}`,
        label: getDayLabel("fitness_preparation", dayKey, `Prep Day ${i}`),
        phase: "fitness_preparation",
        dayKey,
        sections: getMergedSections("fitness_preparation", dayKey),
      });
    }

    // Active days
    if (card.active) {
      Object.keys(card.active).forEach((dayKey) => {
        const num = dayKey.split("-")[1];
        options.push({
          value: `active:${dayKey}`,
          label: getDayLabel("active", dayKey, ` Day ${num}`),
          phase: "active",
          dayKey,
          sections: getMergedSections("active", dayKey),
        });
      });
    }

    // Re-preparation days
    const reprepCount =
      fitnessRePreparationDays > 0
        ? fitnessRePreparationDays
        : card.fitnessRePreparation
          ? Object.keys(card.fitnessRePreparation).length
          : 0;

    for (let i = 1; i <= reprepCount; i++) {
      const dayKey = `day-${i}`;
      options.push({
        value: `fitness_re_preparation:${dayKey}`,
        label: getDayLabel(
          "fitness_re_preparation",
          dayKey,
          `Re-Prep Day ${i}`,
        ),
        phase: "fitness_re_preparation",
        dayKey,
        sections: getMergedSections("fitness_re_preparation", dayKey),
      });
    }

    // Rest days
    const restCount =
      card.rest &&
        typeof card.rest === "object" &&
        Object.keys(card.rest).length > 0
        ? Object.keys(card.rest).length
        : card.restCommon?.sections?.length > 0 ||
          card.restCommonPost?.sections?.length > 0 ||
          card.restCooldown?.sections?.length > 0
          ? 1
          : 0;

    for (let i = 0; i <= restCount; i++) {
      const dayKey = `day-${i}`;
      options.push({
        value: `rest:${dayKey}`,
        label: getDayLabel("rest", dayKey, `Rest Day ${i + 1}`),
        phase: "rest",
        dayKey,
        sections: getMergedSections("rest", dayKey),
      });
    }

    return options.filter(
      (opt) =>
        opt.sections &&
        opt.sections.some(
          (sec) => Array.isArray(sec.workouts) && sec.workouts.length > 0,
        ),
    );
  }, [workoutCard, fitnessPreparationDays, fitnessRePreparationDays]);

  const handleConfirmFlexibleSelection = async () => {
    if (!tempSelected) return;
    const [phase, dayKey] = tempSelected.split(":");
    const token = isStaff ? null : localStorage.getItem("member_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const apiPrefix = isStaff ? "" : "/member-portal";

    setLoading(true);
    try {
      await api.put(
        `${apiPrefix}/workouts/schedule/${localSession.id}/select-flexible-day`,
        { phase, dayKey },
        { headers },
      );
      toast.success("Workout routine updated!");
      setShowFlexibleDialog(false);
      onSuccess?.();
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Failed to update workout routine.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResolveMissedDay = async (status) => {
    const token = isStaff ? null : localStorage.getItem("member_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const apiPrefix = isStaff ? "" : "/member-portal";

    setLoading(true);
    try {
      await api.put(
        `${apiPrefix}/workouts/schedule/${localSession.id}/status`,
        { status },
        { headers },
      );
      toast.success(
        `Workout ${status === "completed" ? "completed" : "skipped"}`,
      );
      onSuccess?.();
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Failed to update workout status.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmResolveMissedDay = (status) => {
    const formattedDate = localSession.date
      ? format(parseLocalDate(localSession.date), "EEEE, MMM d, yyyy")
      : "";
    const isMove = missedWorkoutPolicy === "move_to_next_day";

    setConfirmDialog({
      title: isMove ? "Move Workout" : "Skip Workout",
      description: isMove
        ? `Are you sure you want to move the workout assigned for ${formattedDate} to the ${isFlexible ? "last" : "next"} day?`
        : `Are you sure you want to skip the workout assigned for ${formattedDate}?`,
      onConfirm: () => handleResolveMissedDay(status),
    });
  };

  const getCompleterInfo = () => {
    if (isStaff) {
      return {
        completedBy: user?.id || user?.userId || "staff",
        completedByName: user?.name || "Staff",
      };
    } else {
      const member = JSON.parse(localStorage.getItem("member_data") || "{}");
      return {
        completedBy: member.id
          ? `member:${member.id}`
          : member.userId
            ? `member:${member.userId}`
            : "member",
        completedByName: member.name || "Member",
      };
    }
  };

  const formatCompletionText = (completedBy, completedByName, completedAt) => {
    if (!completedAt) return "";
    const date = new Date(completedAt);
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "pm" : "am";
    hours = hours % 12;
    hours = hours ? hours : 12;
    const isMember = completedBy?.startsWith("member:");
    const name = completedByName || (isMember ? "Member" : "Staff");
    return `Completed by ${name} on ${day} ${month} ${year}, ${hours}:${minutes} ${ampm}`;
  };

  const formatCompletionTimeText = (
    completedBy,
    completedByName,
    completedAt,
  ) => {
    if (!completedAt) return "";
    const date = new Date(completedAt);
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "pm" : "am";
    hours = hours % 12;
    hours = hours ? hours : 12;
    const isMember = completedBy?.startsWith("member:");
    const name = completedByName || (isMember ? "Member" : "Staff");
    return `${name} (${hours}:${minutes} ${ampm})`;
  };

  const isImage = (file) => {
    if (file.type && file.type.startsWith("image/")) return true;
    const url = file.url || "";
    return /\.(jpg|jpeg|png|webp|gif|svg)/i.test(url);
  };

  useEffect(() => {
    if (!activeTimer) {
      setElapsed(0);
      return;
    }
    const interval = setInterval(() => {
      setElapsed((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [activeTimer]);

  const formatTime = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const formatMeaningfulTime = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    if (mins === 0) {
      return `${secs} sec`;
    }
    if (secs === 0) {
      return `${mins} mins`;
    }
    return `${mins} mins ${secs} sec`;
  };

  const handleStartTimer = (secIdx, wIdx, setIdx) => {
    setActiveTimer({ secIdx, wIdx, setIdx });
    setElapsed(0);
  };

  const handleStopTimerAndComplete = (secIdx, wIdx, setIdx) => {
    const sessionCopy = localSession.workoutSession || { sections: [] };
    const section = sessionCopy.sections?.[secIdx];
    const workout = section?.workouts?.[wIdx];
    const workoutDetails = getWorkoutDetails(workout?.workoutId);
    const workoutTitle = workoutDetails?.title || "Exercise";

    const formatted = formatMeaningfulTime(elapsed);
    setConfirmDialog({
      title: "Set Completed! 🎉",
      type: "timer",
      workoutTitle,
      formattedTime: formatted,
      onConfirm: () => {
        setActiveTimer(null);
        setElapsed(0);
        handleToggleSetComplete(secIdx, wIdx, setIdx, true, true);
      },
    });
  };

  useEffect(() => {
    setLocalSession(session);
  }, [session]);

  const getWorkoutDetails = (workoutId) => {
    return localSession.workouts?.find((w) => w.id === workoutId);
  };

  const getLoggedSetDetails = (workout, workoutDetails) => {
    if (workout.loggedSetDetails && workout.loggedSetDetails.length > 0) {
      return workout.loggedSetDetails;
    }
    const suggestions = workout.setDetails || workoutDetails?.setDetails || [];
    return suggestions.map((set) => ({
      ...set,
      completed: false,
    }));
  };

  const sectionsToRender = useMemo(() => {
    if (!localSession) return [];
    const rawSections = localSession.workoutSession?.sections;
    if (rawSections && rawSections.length > 0) {
      return rawSections;
    }
    return [
      {
        name: "Workouts",
        workouts: (localSession.workoutIds || []).map((id) => ({
          workoutId: id,
        })),
      },
    ];
  }, [localSession]);

  const hasWorkouts = useMemo(() => {
    if (!sectionsToRender || sectionsToRender.length === 0) return false;
    return sectionsToRender.some(
      (sec) => sec.workouts && sec.workouts.length > 0,
    );
  }, [sectionsToRender]);

  const { totalExercises, completedExercisesCount } = useMemo(() => {
    if (!sectionsToRender)
      return { totalExercises: 0, completedExercisesCount: 0 };
    let total = 0;
    let completed = 0;
    sectionsToRender.forEach((sec) => {
      (sec.workouts || []).forEach((w) => {
        const details = getWorkoutDetails(w.workoutId);
        if (details) {
          total++;
          if (w.completed) {
            completed++;
          }
        }
      });
    });
    return { totalExercises: total, completedExercisesCount: completed };
  }, [sectionsToRender, localSession.workouts]);

  const completionMetrics = useMemo(() => {
    if (!sectionsToRender || !localSession) return null;

    let totalWeightVolume = 0;
    let maxWeight = 0;
    let totalDistanceKm = 0;
    let totalDurationMin = 0;
    let completedSetsCount = 0;
    let earliestTime = null;
    let latestTime = null;

    sectionsToRender.forEach((sec) => {
      (sec.workouts || []).forEach((w) => {
        const details = getWorkoutDetails(w.workoutId);
        const metric = details?.workoutMetric || w.workoutMetric || "reps";
        const loggedSets = getLoggedSetDetails(w, details);

        loggedSets.forEach((s) => {
          if (s.completed) {
            completedSetsCount++;

            if (s.completedAt) {
              const t = new Date(s.completedAt).getTime();
              if (earliestTime === null || t < earliestTime) earliestTime = t;
              if (latestTime === null || t > latestTime) latestTime = t;
            }

            if (metric === "reps" || !metric) {
              const reps = parseFloat(s.reps) || 0;
              const weight = parseFloat(s.weightKg) || 0;
              totalWeightVolume += reps * weight;
              if (weight > maxWeight) {
                maxWeight = weight;
              }
            } else if (metric === "distance") {
              const dist = parseFloat(s.distanceKm) || 0;
              totalDistanceKm += dist;
            } else if (metric === "duration") {
              const dur = parseFloat(s.durationMin) || 0;
              totalDurationMin += dur;
            }
          }
        });
      });
    });

    if (localSession.completedAt) {
      const t = new Date(localSession.completedAt).getTime();
      if (earliestTime !== null && t > earliestTime) {
        latestTime = t;
      }
    }

    let elapsedSeconds = 0;
    if (earliestTime && latestTime && latestTime > earliestTime) {
      elapsedSeconds = Math.round((latestTime - earliestTime) / 1000);
    }

    return {
      totalWeightVolume,
      maxWeight,
      totalDistanceKm,
      totalDurationMin,
      completedSetsCount,
      elapsedSeconds,
    };
  }, [sectionsToRender, localSession]);

  if (!localSession) return null;

  const st = STATUS_CLS[localSession.status] || STATUS_CLS.upcoming;
  const Icon = st.icon;
  const dateLabel = localSession.date
    ? format(parseLocalDate(localSession.date), "EEEE, MMM d, yyyy")
    : "";

  const handleUpdateWorkoutSession = async (updatedSession) => {
    const updated = {
      ...localSession,
      workoutSession: updatedSession,
    };
    setLocalSession(updated);

    const token = isStaff ? null : localStorage.getItem("member_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const apiPrefix = isStaff ? "" : "/member-portal";

    try {
      await api.put(
        `${apiPrefix}/workouts/schedule/${localSession.id}/session`,
        { workoutSession: updatedSession },
        { headers },
      );

      // 2. Recalculate automatic status changes
      // 2. Recalculate automatic status changes
      let allSetsCompleted = true;
      let totalSetsCount = 0;
      let completedSetsCount = 0;
      let completedWorkoutsCount = 0;
      let totalWorkoutsCount = 0;

      updatedSession.sections?.forEach((sec) => {
        sec.workouts?.forEach((w) => {
          const workoutDetails = localSession.workouts?.find(
            (dw) => dw.id === w.workoutId,
          );
          const loggedSets =
            w.loggedSetDetails || getLoggedSetDetails(w, workoutDetails);
          loggedSets.forEach((set) => {
            totalSetsCount++;
            if (set.completed) {
              completedSetsCount++;
            } else {
              allSetsCompleted = false;
            }
          });
          totalWorkoutsCount++;
          if (w.completed) {
            completedWorkoutsCount++;
          }
        });
      });

      if (totalSetsCount === 0) {
        let allWorkoutsCompleted = true;
        updatedSession.sections?.forEach((sec) => {
          sec.workouts?.forEach((w) => {
            if (!w.completed) {
              allWorkoutsCompleted = false;
            }
          });
        });
        if (totalWorkoutsCount > 0) {
          allSetsCompleted = allWorkoutsCompleted;
          completedSetsCount = completedWorkoutsCount;
        } else {
          allSetsCompleted = false;
        }
      }

      const currentDayStatus = localSession.status;
      let targetStatus = "upcoming";
      if (allSetsCompleted && (totalSetsCount > 0 || totalWorkoutsCount > 0)) {
        targetStatus = "completed";
      } else if (completedSetsCount > 0 || completedWorkoutsCount > 0) {
        targetStatus = "in_progress";
      }

      if (currentDayStatus !== targetStatus) {
        try {
          await api.put(
            `${apiPrefix}/workouts/schedule/${localSession.id}/status`,
            { status: targetStatus },
            { headers },
          );
          if (targetStatus === "completed") {
            toast.success("Day marked as completed!");
          } else if (targetStatus === "in_progress") {
            toast.success("Day marked as in progress!");
          } else {
            toast.success("Day marked as upcoming");
          }
          setLocalSession((prev) => ({ ...prev, status: targetStatus }));
          onSuccess?.();
        } catch (err) {
          toast.error("Failed to update status");
        }
      } else {
        onSuccess?.();
      }
    } catch (err) {
      console.error("Failed to save session updates:", err);
      toast.error("Failed to auto-save workout log");
    }
  };

  const handleToggleSetComplete = (
    sectionIdx,
    workoutIdx,
    setIdx,
    completedVal,
    skipConfirm = false,
  ) => {
    if (!localSession) return;

    // Stop active timer if this set is being toggled
    if (
      activeTimer &&
      activeTimer.secIdx === sectionIdx &&
      activeTimer.wIdx === workoutIdx &&
      activeTimer.setIdx === setIdx
    ) {
      setActiveTimer(null);
      setElapsed(0);
    }

    const sessionCopy = JSON.parse(
      JSON.stringify(localSession.workoutSession || { sections: [] }),
    );
    const workout = sessionCopy.sections[sectionIdx].workouts[workoutIdx];
    const workoutDetails = getWorkoutDetails(workout.workoutId);
    const workoutTitle = workoutDetails?.title || "Exercise";
    const setNumber = setIdx + 1;

    const proceed = () => {
      if (!workout.loggedSetDetails || workout.loggedSetDetails.length === 0) {
        workout.loggedSetDetails = getLoggedSetDetails(workout, workoutDetails);
      }

      const completer = getCompleterInfo();
      const nowStr = new Date().toISOString();

      workout.loggedSetDetails[setIdx].completed = completedVal;
      workout.loggedSetDetails[setIdx].completedAt = completedVal
        ? nowStr
        : null;
      workout.loggedSetDetails[setIdx].completedBy = completedVal
        ? completer.completedBy
        : null;
      workout.loggedSetDetails[setIdx].completedByName = completedVal
        ? completer.completedByName
        : null;

      // Check if all sets completed
      const allSetsChecked = workout.loggedSetDetails.every((s) => s.completed);
      workout.completed = allSetsChecked;
      workout.completedAt = allSetsChecked ? nowStr : null;
      workout.completedBy = allSetsChecked ? completer.completedBy : null;
      workout.completedByName = allSetsChecked
        ? completer.completedByName
        : null;

      return handleUpdateWorkoutSession(sessionCopy);
    };

    if (skipConfirm) {
      return proceed();
    } else {
      setConfirmDialog({
        title: completedVal ? "Complete Set" : "Uncheck Set",
        type: "set",
        action: completedVal ? "complete" : "reset",
        workoutTitle,
        setNumber,
        onConfirm: proceed,
      });
    }
  };

  const handleToggleWorkoutComplete = (
    sectionIdx,
    workoutIdx,
    completedVal,
  ) => {
    if (!localSession) return;
    const sessionCopy = JSON.parse(
      JSON.stringify(localSession.workoutSession || { sections: [] }),
    );
    const workout = sessionCopy.sections[sectionIdx].workouts[workoutIdx];
    const workoutDetails = getWorkoutDetails(workout.workoutId);
    const workoutTitle = workoutDetails?.title || "Exercise";

    setConfirmDialog({
      title: completedVal ? "Complete Exercise" : "Reset Exercise",
      type: "workout",
      action: completedVal ? "complete" : "reset",
      workoutTitle,
      onConfirm: () => {
        workout.completed = completedVal;

        if (
          !workout.loggedSetDetails ||
          workout.loggedSetDetails.length === 0
        ) {
          workout.loggedSetDetails = getLoggedSetDetails(
            workout,
            workoutDetails,
          );
        }

        const completer = getCompleterInfo();
        const nowStr = new Date().toISOString();

        workout.completedAt = completedVal ? nowStr : null;
        workout.completedBy = completedVal ? completer.completedBy : null;
        workout.completedByName = completedVal
          ? completer.completedByName
          : null;

        workout.loggedSetDetails.forEach((s) => {
          s.completed = completedVal;
          s.completedAt = completedVal ? nowStr : null;
          s.completedBy = completedVal ? completer.completedBy : null;
          s.completedByName = completedVal ? completer.completedByName : null;
        });

        return handleUpdateWorkoutSession(sessionCopy);
      },
    });
  };

  const handleToggleSectionComplete = (sectionIdx, completedVal) => {
    if (!localSession) return;
    const sessionCopy = JSON.parse(
      JSON.stringify(localSession.workoutSession || { sections: [] }),
    );
    const section = sessionCopy.sections[sectionIdx];
    const sectionName = section?.name || "Section";

    setConfirmDialog({
      title: completedVal ? "Complete Section" : "Reset Section",
      type: "section",
      action: completedVal ? "complete" : "reset",
      sectionName,
      onConfirm: () => {
        const completer = getCompleterInfo();
        const nowStr = new Date().toISOString();

        section.completed = completedVal;
        section.completedAt = completedVal ? nowStr : null;
        section.completedBy = completedVal ? completer.completedBy : null;
        section.completedByName = completedVal
          ? completer.completedByName
          : null;

        section.workouts?.forEach((w) => {
          w.completed = completedVal;
          w.completedAt = completedVal ? nowStr : null;
          w.completedBy = completedVal ? completer.completedBy : null;
          w.completedByName = completedVal ? completer.completedByName : null;

          const workoutDetails = getWorkoutDetails(w.workoutId);
          if (!w.loggedSetDetails || w.loggedSetDetails.length === 0) {
            w.loggedSetDetails = getLoggedSetDetails(w, workoutDetails);
          }
          w.loggedSetDetails.forEach((s) => {
            s.completed = completedVal;
            s.completedAt = completedVal ? nowStr : null;
            s.completedBy = completedVal ? completer.completedBy : null;
            s.completedByName = completedVal ? completer.completedByName : null;
          });
        });

        return handleUpdateWorkoutSession(sessionCopy);
      },
    });
  };

  const handleUpdateSetMetric = (
    sectionIdx,
    workoutIdx,
    setIdx,
    field,
    val,
  ) => {
    if (!localSession) return;
    const sessionCopy = JSON.parse(
      JSON.stringify(localSession.workoutSession || { sections: [] }),
    );
    const workout = sessionCopy.sections[sectionIdx].workouts[workoutIdx];
    const workoutDetails = getWorkoutDetails(workout.workoutId);

    if (!workout.loggedSetDetails || workout.loggedSetDetails.length === 0) {
      workout.loggedSetDetails = getLoggedSetDetails(workout, workoutDetails);
    }

    workout.loggedSetDetails[setIdx][field] = val;
    handleUpdateWorkoutSession(sessionCopy);
  };

  const handleMarkCompleteBulk = async () => {
    setConfirmDialog({
      title: "Complete Entire Session",
      type: "session-bulk",
      action: "complete",
      description:
        "Are you sure you want to mark this entire workout session as Completed? This will automatically mark all sections, exercises, and sets as completed.",
      onConfirm: async () => {
        const token = isStaff ? null : localStorage.getItem("member_token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const apiPrefix = isStaff ? "" : "/member-portal";

        setLoading(true);
        try {
          if (localSession.workoutSession) {
            const sessionCopy = JSON.parse(
              JSON.stringify(localSession.workoutSession || { sections: [] }),
            );
            const completer = getCompleterInfo();
            const nowStr = new Date().toISOString();

            sessionCopy.sections?.forEach((sec) => {
              sec.completed = true;
              sec.completedAt = nowStr;
              sec.completedBy = completer.completedBy;
              sec.completedByName = completer.completedByName;

              sec.workouts?.forEach((w) => {
                w.completed = true;
                w.completedAt = nowStr;
                w.completedBy = completer.completedBy;
                w.completedByName = completer.completedByName;

                if (!w.loggedSetDetails || w.loggedSetDetails.length === 0) {
                  const workoutDetails = localSession.workouts?.find(
                    (dw) => dw.id === w.workoutId,
                  );
                  w.loggedSetDetails = getLoggedSetDetails(w, workoutDetails);
                }
                w.loggedSetDetails.forEach((s) => {
                  s.completed = true;
                  s.completedAt = nowStr;
                  s.completedBy = completer.completedBy;
                  s.completedByName = completer.completedByName;
                });
              });
            });
            await api.put(
              `${apiPrefix}/workouts/schedule/${localSession.id}/session`,
              { workoutSession: sessionCopy },
              { headers },
            );
          }
          await api.put(
            `${apiPrefix}/workouts/schedule/${localSession.id}/status`,
            { status: "completed" },
            { headers },
          );
          toast.success("Workout marked as completed");
          onSuccess?.();
        } catch (err) {
          toast.error(err.response?.data?.detail || "Failed to update status");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleMarkUncompleteBulk = async () => {
    setConfirmDialog({
      title: "Reset Entire Session",
      type: "session-bulk",
      action: "reset",
      description:
        "Are you sure you want to mark this entire workout session as Incomplete? This will reset all exercises and sets to incomplete.",
      onConfirm: async () => {
        const token = isStaff ? null : localStorage.getItem("member_token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const apiPrefix = isStaff ? "" : "/member-portal";

        setLoading(true);
        try {
          if (localSession.workoutSession) {
            const sessionCopy = JSON.parse(
              JSON.stringify(localSession.workoutSession || { sections: [] }),
            );
            sessionCopy.sections?.forEach((sec) => {
              sec.completed = false;
              sec.completedAt = null;
              sec.completedBy = null;
              sec.completedByName = null;

              sec.workouts?.forEach((w) => {
                w.completed = false;
                w.completedAt = null;
                w.completedBy = null;
                w.completedByName = null;

                if (!w.loggedSetDetails || w.loggedSetDetails.length === 0) {
                  const workoutDetails = localSession.workouts?.find(
                    (dw) => dw.id === w.workoutId,
                  );
                  w.loggedSetDetails = getLoggedSetDetails(w, workoutDetails);
                }
                w.loggedSetDetails.forEach((s) => {
                  s.completed = false;
                  s.completedAt = null;
                  s.completedBy = null;
                  s.completedByName = null;
                });
              });
            });
            await api.put(
              `${apiPrefix}/workouts/schedule/${localSession.id}/session`,
              { workoutSession: sessionCopy },
              { headers },
            );
          }
          await api.put(
            `${apiPrefix}/workouts/schedule/${localSession.id}/status`,
            { status: "upcoming" },
            { headers },
          );
          toast.success("Workout marked as uncompleted");
          onSuccess?.();
        } catch (err) {
          toast.error(err.response?.data?.detail || "Failed to update status");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  return (
    <>
      <Card className="border-0 shadow-sm">
        {!hideHeader && (
          <CardHeader className="sticky top-[-8px] lg:top-[-24px] z-20 bg-card/95 backdrop-blur-md px-1 py-2 mb-2 border-b border-border/40 flex flex-row items-center justify-between gap-3">
            <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground font-black flex items-center gap-2">
              <CalendarIcon className="h-4 w-4 text-primary" />
              <span>{dateLabel}</span>
            </CardTitle>
            <div className="flex items-center gap-2">
              <span
                className={`inline-flex items-center gap-1 text-[10px] font-heading font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0 ${st.light} ${st.text}`}
              >
                <Icon className="h-3 w-3" />
                {st.label}
              </span>
            </div>
          </CardHeader>
        )}

        <CardContent className="p-0 space-y-1">
          {isSessionMissed && (allowMemberToComplete || isStaff) ? (
            <Card className="border-blue-500/30 bg-blue-500/5 dark:bg-blue-950/10 shadow-sm border mb-3">
              <CardContent className="p-3 space-y-3">
                <div className="flex items-start gap-2 text-left">
                  <AlertCircle className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" />
                  <div className="text-xs text-muted-foreground space-y-1">
                    {missedWorkoutPolicy === "move_to_next_day" ? (
                      <p>
                        This workout day was missed. To resolve it, you can
                        either{" "}
                        <strong>choose a workout routine and log it</strong>, or
                        click the button to{" "}
                        <strong>
                          {isFlexible
                            ? "move it to the last day"
                            : "move it to the next day"}
                        </strong>
                        .
                      </p>
                    ) : (
                      <p>
                        This workout day was missed. To resolve it, you can
                        either{" "}
                        <strong>choose a workout routine and log it</strong>, or
                        click the button to <strong>skip this workout</strong>.
                      </p>
                    )}

                    {isFlexible && localSession.isFlexibleChosen && (
                      <div className="flex flex-wrap items-center gap-1.5 pt-1.5">
                        <span className="text-xs font-semibold text-foreground">
                          Selected:{" "}
                          {localSession.phase === "active"
                            ? "Active"
                            : localSession.phase === "fitness_preparation"
                              ? "Preparation"
                              : localSession.phase === "fitness_re_preparation"
                                ? "Re-Preparation"
                                : "Rest"}{" "}
                          Day {localSession.workoutDayNumber || 1}
                        </span>
                        {localSession.workoutSession?.sections?.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {localSession.workoutSession.sections.map(
                              (sec, sIdx) => {
                                const wCount = sec.workouts?.length || 0;
                                if (wCount === 0) return null;
                                const sourceLabels = {
                                  global: "Global",
                                  phase_common: "Pre",
                                  day_plan: "Day",
                                  phase_common_post: "Post",
                                };
                                const prefix =
                                  sourceLabels[sec.source] || "Day";
                                return (
                                  <Badge
                                    key={sIdx}
                                    variant="outline"
                                    className="text-[9px] bg-secondary/50 py-0 uppercase"
                                  >
                                    {prefix}: {sec.name} ({wCount})
                                  </Badge>
                                );
                              },
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto pt-1">
                  {isFlexible && (
                    <button
                      onClick={() => {
                        const currentVal = localSession.isFlexibleChosen
                          ? `${localSession.phase}:${localSession.workoutDayNumber ? `day-${localSession.workoutDayNumber}` : "day-1"}`
                          : "none:none";
                        setTempSelected(currentVal);
                        setShowFlexibleDialog(true);
                      }}
                      disabled={loading}
                      className="flex-1 sm:flex-none py-1.5 px-3 rounded-lg bg-primary text-primary-foreground hover:bg-primary/95 text-xs font-bold transition-all flex items-center justify-center gap-1.5 shadow cursor-pointer border-0 disabled:opacity-50"
                    >
                      <Shuffle className="h-3.5 w-3.5" />
                      {localSession.isFlexibleChosen
                        ? "Change Workouts"
                        : "Choose Workouts"}
                    </button>
                  )}

                  {missedWorkoutPolicy === "move_to_next_day" ? (
                    <button
                      onClick={() => handleConfirmResolveMissedDay("skipped")}
                      disabled={loading}
                      className="flex-1 sm:flex-none py-1.5 px-3 rounded-lg bg-blue-500 text-white hover:bg-blue-600 text-xs font-bold transition-all flex items-center justify-center gap-1 shadow cursor-pointer border-0 disabled:opacity-50"
                    >
                      <Clock className="h-3.5 w-3.5" />
                      {isFlexible ? "Move to Last Day" : "Move to Next Day"}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleConfirmResolveMissedDay("skipped")}
                      disabled={loading}
                      className="flex-1 sm:flex-none py-1.5 px-3 rounded-lg bg-amber-500 text-white hover:bg-amber-600 text-xs font-bold transition-all flex items-center justify-center gap-1 shadow cursor-pointer border-0 disabled:opacity-50"
                    >
                      <XCircle className="h-3.5 w-3.5" />
                      Skip Workout
                    </button>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            isFlexible &&
            (!localSession.isFlexibleChosen ||
              totalExercises === 0 ||
              completedExercisesCount !== totalExercises) && (
              <Card className="border-border bg-card/50 backdrop-blur shadow-sm mb-3">
                <CardContent className="p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-0">
                  <div className="min-w-0 flex-1">
                    <h4 className="text-xs font-bold flex items-center gap-1.5 text-primary uppercase tracking-wider hidden">
                      <Shuffle className="h-3.5 w-3.5 animate-spin-slow" />{" "}
                      Choose Your Workout
                    </h4>
                    {localSession.isFlexibleChosen ? (
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5 mb-2">
                        <span className="text-xs font-bold uppercase text-foreground">
                          Selected:{" "}
                          {localSession.phase === "active"
                            ? ""
                            : localSession.phase === "fitness_preparation"
                              ? "Preparation"
                              : localSession.phase === "fitness_re_preparation"
                                ? "Re-Preparation"
                                : "Rest"}{" "}
                          <span className="text-xs font-bold uppercase text-foreground bg-primary/40 px-2 rounded-lg">
                            {" "}
                            Day {localSession.workoutDayNumber || 1}
                          </span>
                        </span>
                        {localSession.workoutSession?.sections?.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {localSession.workoutSession.sections.map(
                              (sec, sIdx) => {
                                const wCount = sec.workouts?.length || 0;
                                if (wCount === 0) return null;
                                const sourceLabels = {
                                  global: "Global",
                                  phase_common: "Pre",
                                  day_plan: "Day",
                                  phase_common_post: "Post",
                                };
                                const prefix =
                                  sourceLabels[sec.source] || "Day";
                                return (
                                  <Badge
                                    key={sIdx}
                                    variant="outline"
                                    className="text-[9px] bg-secondary/50 py-0 uppercase"
                                  >
                                    {prefix}: {sec.name} ({wCount})
                                  </Badge>
                                );
                              },
                            )}
                          </div>
                        ) : (
                          <span className="text-[10px] text-muted-foreground italic">
                            (Rest / Recovery)
                          </span>
                        )}
                      </div>
                    ) : (
                      <p className="text-[10px] text-muted-foreground mt-0.5 hidden">
                        No workout routine chosen for this day yet.
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      const currentVal = localSession.isFlexibleChosen
                        ? `${localSession.phase}:${localSession.workoutDayNumber ? `day-${localSession.workoutDayNumber}` : "day-1"}`
                        : "none:none";
                      setTempSelected(currentVal);
                      setShowFlexibleDialog(true);
                    }}
                    disabled={loading}
                    className="w-full sm:w-auto py-1.5 px-3.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/95 text-xs font-bold transition-all flex items-center justify-center gap-1.5 shadow cursor-pointer border-0 shrink-0"
                  >
                    <Shuffle className="h-3.5 w-3.5" />
                    {localSession.isFlexibleChosen
                      ? "Change the workouts for this day"
                      : "Choose the workouts for this day"}
                  </button>
                </CardContent>
              </Card>
            )
          )}

          {isLocked && session?.date === todayStr && (
            <Card className="border-amber-500 bg-amber-500/5 dark:bg-amber-950/10 shadow-sm border mb-3">
              <CardContent className="p-3 flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 animate-bounce" />
                <div className="text-xs text-muted-foreground">
                  <strong>Logging is locked:</strong> You have{" "}
                  <strong>{missedCount} past missed workouts</strong>. To start
                  logging today's workout, you must first complete, skip, or
                  move those missed workouts.
                </div>
              </CardContent>
            </Card>
          )}

          {!isLocked &&
            isFlexible &&
            allowMemberToComplete &&
            missedCount > 0 &&
            session?.date >= todayStr &&
            session?.status === "upcoming" && (
              <Card className="border-amber-500 bg-amber-500/5 dark:bg-amber-950/10 shadow-sm border mb-3">
                <CardContent className="p-3 flex items-center gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 animate-bounce" />
                  <div className="text-xs text-muted-foreground">
                    <strong>Missed Workouts:</strong> You have{" "}
                    <strong>{missedCount} past missed workouts</strong>. Please
                    resolve them (complete, skip, or move) to monitor and ensure
                    correct progress tracking.
                  </div>
                </CardContent>
              </Card>
            )}

          {localSession.status === "completed" &&
            localSession.completedAt &&
            (() => {
              const date = new Date(localSession.completedAt);
              const months = [
                "Jan",
                "Feb",
                "Mar",
                "Apr",
                "May",
                "Jun",
                "Jul",
                "Aug",
                "Sep",
                "Oct",
                "Nov",
                "Dec",
              ];
              const day = date.getDate();
              const month = months[date.getMonth()];
              const year = date.getFullYear();
              let hours = date.getHours();
              const minutes = String(date.getMinutes()).padStart(2, "0");
              const ampm = hours >= 12 ? "pm" : "am";
              hours = hours % 12;
              hours = hours ? hours : 12;
              const displayDateTime = `${day} ${month} ${year}, ${hours}:${minutes} ${ampm}`;
              const isMember = localSession.completedBy?.startsWith("member:");

              return (
                <div className="text-xs flex-col tracking-wider font-semibold flex items-start text-muted-foreground space-y-0.5 border bg-secondary/10 border-border/40 p-2 rounded-lg">
                  <p className="text-foreground">
                    Done by {isMember ? "Member" : "Staff"}:{" "}
                    {localSession.completedByName ||
                      (isMember ? "Member" : "Staff")}
                  </p>
                  <p className="text-muted-foreground font-medium">
                    {displayDateTime}
                  </p>
                </div>
              );
            })()}

          {hasWorkouts ? (
            <div className="space-y-2">
              {isCompletionAllowed && totalExercises > 0 && (
                <div
                  className={cn(
                    "sticky z-20 bg-card/95 backdrop-blur-md py-3 px-1 mb-3 border-b border-border/10 flex items-center gap-3 text-xs text-muted-foreground font-mono",
                    hideHeader
                      ? "top-[-8px] lg:top-[-24px]"
                      : "top-[34px] lg:top-[8px]",
                  )}
                >
                  <div className="flex-1 h-1.5 bg-border rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 transition-all duration-300"
                      style={{
                        width: `${(completedExercisesCount / totalExercises) * 100}%`,
                      }}
                    />
                  </div>
                  <span className="shrink-0 font-semibold text-foreground">
                    {completedExercisesCount}/{totalExercises} exercises
                  </span>
                </div>
              )}
              {completedExercisesCount === totalExercises &&
                totalExercises > 0 &&
                completionMetrics && (
                  <div className="bg-gradient-to-br from-emerald-500/10 via-teal-500/5 to-transparent border border-emerald-500/25 rounded-2xl p-5 mb-4 shadow-sm relative overflow-hidden backdrop-blur-sm transition-all duration-300 hover:shadow-md hover:border-emerald-500/40">
                    {/* Decorative faint background icons or gradient blob */}
                    <div className="absolute right-0 bottom-0 translate-x-4 translate-y-4 opacity-5 pointer-events-none">
                      <Trophy className="h-40 w-40 text-emerald-500" />
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="h-12 w-12 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0 shadow-inner animate-pulse">
                        <Trophy className="h-6 w-6 text-emerald-500 dark:text-emerald-400" />
                      </div>
                      <div className="space-y-1 select-text text-left">
                        <h3 className="text-base font-heading font-extrabold tracking-tight text-foreground flex items-center gap-1.5">
                          Workout Day Completed! 🎉
                        </h3>
                        <p className="text-xs text-muted-foreground font-medium">
                          Phenomenal job! You crushed today's routine. Here is a
                          summary of your achievements:
                        </p>
                      </div>
                    </div>

                    {/* Metrics grid */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4 pt-2">
                      {completionMetrics.totalWeightVolume > 0 && (
                        <div className="bg-card/40 border border-border/50 rounded-xl p-3 flex flex-col justify-between text-left transition-all hover:bg-card/65">
                          <div className="flex items-center gap-1.5 text-muted-foreground">
                            <Weight className="h-4 w-4 text-emerald-500" />
                            <span className="text-[10px] font-semibold uppercase tracking-wider">
                              Weight Lifted
                            </span>
                          </div>
                          <div className="mt-2">
                            <span className="text-base font-mono font-black text-foreground">
                              {completionMetrics.totalWeightVolume.toLocaleString()}
                            </span>
                            <span className="text-[11px] font-bold text-muted-foreground ml-0.5">
                              kg
                            </span>
                          </div>
                          {completionMetrics.maxWeight > 0 && (
                            <div className="text-[10px] text-muted-foreground/80 mt-1 font-medium font-sans">
                              Max Lift:{" "}
                              <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">
                                {completionMetrics.maxWeight} kg
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      {completionMetrics.totalDistanceKm > 0 && (
                        <div className="bg-card/40 border border-border/50 rounded-xl p-3 flex flex-col justify-between text-left transition-all hover:bg-card/65">
                          <div className="flex items-center gap-1.5 text-muted-foreground">
                            <Route className="h-4 w-4 text-teal-500" />
                            <span className="text-[10px] font-semibold uppercase tracking-wider">
                              Distance
                            </span>
                          </div>
                          <div className="mt-2">
                            <span className="text-base font-mono font-black text-foreground">
                              {completionMetrics.totalDistanceKm.toFixed(2)}
                            </span>
                            <span className="text-[11px] font-bold text-muted-foreground ml-0.5">
                              km
                            </span>
                          </div>
                          <div className="text-[10px] text-muted-foreground/80 mt-1 font-medium font-sans">
                            Cardio track
                          </div>
                        </div>
                      )}

                      {(completionMetrics.totalDurationMin > 0 ||
                        completionMetrics.elapsedSeconds > 30) && (
                          <div className="bg-card/40 border border-border/50 rounded-xl p-3 flex flex-col justify-between text-left transition-all hover:bg-card/65">
                            <div className="flex items-center gap-1.5 text-muted-foreground">
                              <Clock className="h-4 w-4 text-amber-500" />
                              <span className="text-[10px] font-semibold uppercase tracking-wider">
                                Time Taken
                              </span>
                            </div>
                            <div className="mt-2">
                              <span className="text-base font-mono font-black text-foreground">
                                {completionMetrics.elapsedSeconds > 30
                                  ? completionMetrics.elapsedSeconds >= 3600
                                    ? `${Math.floor(completionMetrics.elapsedSeconds / 3600)}h ${Math.floor((completionMetrics.elapsedSeconds % 3600) / 60)}m`
                                    : `${Math.floor(completionMetrics.elapsedSeconds / 60)}m ${completionMetrics.elapsedSeconds % 60}s`
                                  : `${completionMetrics.totalDurationMin} mins`}
                              </span>
                            </div>
                            {completionMetrics.elapsedSeconds > 30 &&
                              completionMetrics.totalDurationMin > 0 && (
                                <div className="text-[10px] text-muted-foreground/80 mt-1 font-medium font-sans">
                                  Active duration:{" "}
                                  <span className="font-mono font-bold">
                                    {completionMetrics.totalDurationMin} mins
                                  </span>
                                </div>
                              )}
                            {!(completionMetrics.elapsedSeconds > 30) && (
                              <div className="text-[10px] text-muted-foreground/80 mt-1 font-medium font-sans">
                                Set duration metric
                              </div>
                            )}
                          </div>
                        )}

                      <div className="bg-card/40 border border-border/50 rounded-xl p-3 flex flex-col justify-between text-left transition-all hover:bg-card/65">
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <TrendingUp className="h-4 w-4 text-blue-500" />
                          <span className="text-[10px] font-semibold uppercase tracking-wider">
                            Volume & Sets
                          </span>
                        </div>
                        <div className="mt-2">
                          <span className="text-base font-mono font-black text-foreground">
                            {completionMetrics.completedSetsCount}
                          </span>
                          <span className="text-[11px] font-bold text-muted-foreground ml-0.5">
                            Sets done
                          </span>
                        </div>
                        <div className="text-[10px] text-muted-foreground/80 mt-1 font-medium font-sans">
                          {totalExercises} exercises completed
                        </div>
                      </div>
                    </div>

                    {/* Motivational message dynamic quote block */}
                    <div className="mt-4 pt-3 border-t border-emerald-500/15 flex items-start gap-2 text-left">
                      <span className="text-base select-none mt-0.5">💡</span>
                      <p className="text-[11px] text-muted-foreground leading-relaxed italic font-medium">
                        {(() => {
                          if (completionMetrics.totalWeightVolume > 3000) {
                            return "Incredible power! Lifting over 3,000 kg of volume is top-tier strength work. Keep raising the bar!";
                          } else if (
                            completionMetrics.totalWeightVolume > 1000
                          ) {
                            return "Amazing strength volume! Moving over 1,000 kg is a serious milestone. Your muscles are thanking you!";
                          } else if (completionMetrics.totalDistanceKm > 5) {
                            return "Spectacular stamina! Completing more than 5 km is an outstanding cardio achievement. You're building a bulletproof heart!";
                          } else if (completionMetrics.totalDistanceKm > 2) {
                            return "Awesome endurance! Over 2 km covered shows great dedication and consistent pacing.";
                          } else if (
                            completionMetrics.totalDurationMin > 45 ||
                            completionMetrics.elapsedSeconds > 2700
                          ) {
                            return "Superb dedication! A workout exceeding 45 minutes builds solid stamina, character, and consistency.";
                          } else {
                            return "Consistency is the foundation of greatness. Every set, rep, and minute counts towards your ultimate goal. Rest well and crush the next one!";
                          }
                        })()}
                      </p>
                    </div>
                  </div>
                )}
              {sectionsToRender.map((section, sIdx) => {
                const sectionWorkouts = (section.workouts || [])
                  .map((w, wIdx) => {
                    const details = getWorkoutDetails(w.workoutId);
                    return { ...w, details, wIdx };
                  })
                  .filter((w) => w.details);

                if (sectionWorkouts.length === 0) return null;

                const sectionKey =
                  section.id ||
                  `${sIdx}-${section.name}-${section.source || ""}`;
                const isCollapsed = !expandedSections[sectionKey];
                const completedCount = sectionWorkouts.filter(
                  (w) => w.completed,
                ).length;
                const isSectionCompleted =
                  sectionWorkouts.length > 0 &&
                  completedCount === sectionWorkouts.length;

                return (
                  <div
                    key={section.id || sIdx}
                    id={`section-container-${sectionKey}`}
                    className={cn(
                      "space-y-2 px-2 py-4 rounded-lg border select-none scroll-mt-4 transition-all duration-200",
                      isSectionCompleted
                        ? "bg-emerald-500/5 dark:bg-emerald-500/10 border-emerald-500/20"
                        : localSession.status === "missed"
                          ? "bg-red-500/5 dark:bg-red-500/10 border-red-500/20"
                          : "bg-secondary/100 border-border/40",
                    )}
                  >
                    {/* Section Header */}
                    <div
                      onClick={() =>
                        setExpandedSections((prev) => {
                          const wasExpanded = !!prev[sectionKey];
                          if (!wasExpanded) {
                            setTimeout(() => {
                              const el = document.getElementById(
                                `section-container-${sectionKey}`,
                              );
                              el?.scrollIntoView({
                                behavior: "smooth",
                                block: "start",
                              });
                            }, 150);
                            return { [sectionKey]: true };
                          } else {
                            return {};
                          }
                        })
                      }
                      className="flex items-center justify-between cursor-pointer hover:bg-secondary/20 px-1 rounded transition-colors group select-none"
                    >
                      <div className="flex items-center gap-2">
                        <ChevronDown
                          className={cn(
                            "h-3.5 w-3.5 text-muted-foreground transition-transform duration-200",
                            isCollapsed && "-rotate-90",
                          )}
                        />
                        {isCompletionAllowed && (
                          <Checkbox
                            checked={
                              sectionWorkouts.length > 0 &&
                              sectionWorkouts.every((w) => w.completed)
                            }
                            onCheckedChange={(checked) => {
                              const secIdxInFull =
                                localSession.workoutSession?.sections?.findIndex(
                                  (sec) =>
                                    sec.name === section.name &&
                                    sec.source === section.source,
                                ) ?? -1;
                              if (secIdxInFull !== -1) {
                                handleToggleSectionComplete(
                                  secIdxInFull,
                                  !!checked,
                                );
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="h-5 w-5 text-primary border-muted-foreground/45 rounded mr-0.5"
                          />
                        )}
                        <div className="flex flex-col">
                          <h4
                            className={cn(
                              "text-sm font-heading font-bold  uppercase tracking-wider flex items-center gap-1.5",
                              isSectionCompleted
                                ? "text-emerald-600 dark:text-emerald-400"
                                : localSession.status === "missed"
                                  ? "text-red-600 dark:text-red-400"
                                  : "text-muted-foreground",
                            )}
                          >
                            <span>{section.name || "Workout Section"}</span>
                          </h4>
                          {sectionWorkouts.length > 0 &&
                            sectionWorkouts.every((w) => w.completed) &&
                            section.completedAt && (
                              <span className="text-[9px] text-muted-foreground font-mono font-normal tracking-tight normal-case mt-0.5">
                                {formatCompletionText(
                                  section.completedBy,
                                  section.completedByName,
                                  section.completedAt,
                                )}
                              </span>
                            )}
                        </div>
                      </div>
                      <div
                        className={cn(
                          "text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ml-auto mr-2 shrink-0 flex items-center gap-1",
                          isSectionCompleted
                            ? "bg-emerald-500/10 dark:bg-emerald-500/20"
                            : localSession.status === "missed"
                              ? "bg-red-500/10 dark:bg-red-500/20"
                              : "bg-secondary",
                        )}
                      >
                        <span
                          className={
                            completedCount > 0
                              ? "text-emerald-500 dark:text-emerald-400"
                              : "text-muted-foreground/60"
                          }
                        >
                          {completedCount} done
                        </span>
                        <span className="text-muted-foreground/30">•</span>
                        <span
                          className={
                            sectionWorkouts.length - completedCount > 0
                              ? "text-amber-500 dark:text-amber-400"
                              : "text-muted-foreground/60"
                          }
                        >
                          {sectionWorkouts.length - completedCount} left
                        </span>
                      </div>
                    </div>

                    {!isCollapsed && (
                      <div className="space-y-2 mt-2">
                        {sectionWorkouts.map(
                          ({
                            details: workout,
                            completed,
                            loggedSetDetails,
                            wIdx,
                          }) => {
                            const loggedSets = getLoggedSetDetails(
                              { loggedSetDetails },
                              workout,
                            );
                            const secIdxInFull =
                              localSession.workoutSession?.sections?.findIndex(
                                (sec) =>
                                  sec.name === section.name &&
                                  sec.source === section.source,
                              ) ?? -1;
                            const isWorkoutCompleted = !!completed;
                            const workoutKey = `${sectionKey}-${workout.id || wIdx}-${wIdx}`;

                            return (
                              <div
                                key={workout.id}
                                id={`workout-container-${workoutKey}`}
                                className={cn(
                                  "border rounded-xl scroll-mt-4 transition-all duration-200",
                                  isWorkoutCompleted
                                    ? "bg-emerald-500/5 border-emerald-500/20"
                                    : localSession.status === "missed"
                                      ? "bg-red-500/5 border-red-500/20"
                                      : "bg-card border-border/80",
                                )}
                              >
                                {/* Workout Header */}
                                <div
                                  onClick={() => {
                                    setExpandedWorkoutId((prev) => {
                                      const wasExpanded = prev === workoutKey;
                                      if (!wasExpanded) {
                                        setTimeout(() => {
                                          const el = document.getElementById(
                                            `workout-container-${workoutKey}`,
                                          );
                                          el?.scrollIntoView({
                                            behavior: "smooth",
                                            block: "start",
                                          });
                                        }, 150);
                                        return workoutKey;
                                      } else {
                                        return null;
                                      }
                                    });
                                  }}
                                  className="p-3 flex  justify-between gap-1 cursor-pointer hover:bg-secondary/10 transition-colors select-none flex-col"
                                >
                                  <div className="w-full flex items-center gap-1">
                                    {isCompletionAllowed &&
                                      secIdxInFull !== -1 && (
                                        <Checkbox
                                          checked={isWorkoutCompleted}
                                          onCheckedChange={(checked) => {
                                            const secIdxInFull =
                                              localSession.workoutSession?.sections?.findIndex(
                                                (sec) =>
                                                  sec.name === section.name &&
                                                  sec.source === section.source,
                                              ) ?? -1;
                                            if (secIdxInFull !== -1) {
                                              handleToggleWorkoutComplete(
                                                secIdxInFull,
                                                wIdx,
                                                !!checked,
                                              );
                                            }
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                          className="h-5 w-5 text-primary border-muted-foreground/45 rounded"
                                        />
                                      )}
                                    <div
                                      className={cn(
                                        "font-semibold text-[13px]",
                                        isWorkoutCompleted
                                          ? "line-through text-muted-foreground"
                                          : "text-foreground",
                                      )}
                                    >
                                      {workout.title}
                                    </div>
                                  </div>

                                  <div className="w-full flex items-center justify-between gap-1">
                                    <div className="flex items-center gap-2">

                                      <div className="flex items-center gap-2">
                                        <div className="flex flex-col">

                                          <span className="text-[10px] text-muted-foreground font-mono font-medium mt-0.5">
                                            {(() => {
                                              const numSets = loggedSets.length;
                                              let repsLabel = "";
                                              if (
                                                workout.workoutMetric ===
                                                "reps" ||
                                                !workout.workoutMetric
                                              ) {
                                                const repsList = loggedSets
                                                  .map((s) => s.reps)
                                                  .filter(
                                                    (r) =>
                                                      r !== undefined &&
                                                      r !== null &&
                                                      r !== "",
                                                  );
                                                if (repsList.length > 0) {
                                                  const minReps = Math.min(
                                                    ...repsList,
                                                  );
                                                  const maxReps = Math.max(
                                                    ...repsList,
                                                  );
                                                  repsLabel =
                                                    minReps === maxReps
                                                      ? `${minReps} Reps`
                                                      : `${minReps}-${maxReps} Reps`;
                                                }
                                              } else if (
                                                workout.workoutMetric ===
                                                "duration"
                                              ) {
                                                const durList = loggedSets
                                                  .map((s) => s.durationMin)
                                                  .filter(
                                                    (d) =>
                                                      d !== undefined &&
                                                      d !== null &&
                                                      d !== "",
                                                  );
                                                if (durList.length > 0) {
                                                  const minDur = Math.min(
                                                    ...durList,
                                                  );
                                                  const maxDur = Math.max(
                                                    ...durList,
                                                  );
                                                  repsLabel =
                                                    minDur === maxDur
                                                      ? `${minDur} mins`
                                                      : `${minDur}-${maxDur} mins`;
                                                }
                                              } else if (
                                                workout.workoutMetric ===
                                                "distance"
                                              ) {
                                                const distList = loggedSets
                                                  .map((s) => s.distanceKm)
                                                  .filter(
                                                    (d) =>
                                                      d !== undefined &&
                                                      d !== null &&
                                                      d !== "",
                                                  );
                                                if (distList.length > 0) {
                                                  const minDist = Math.min(
                                                    ...distList,
                                                  );
                                                  const maxDist = Math.max(
                                                    ...distList,
                                                  );
                                                  repsLabel =
                                                    minDist === maxDist
                                                      ? `${minDist} km`
                                                      : `${minDist}-${maxDist} km`;
                                                }
                                              } else if (
                                                workout.workoutMetric === "count"
                                              ) {
                                                const countList = loggedSets
                                                  .map((s) => s.count)
                                                  .filter(
                                                    (c) =>
                                                      c !== undefined &&
                                                      c !== null &&
                                                      c !== "",
                                                  );
                                                if (countList.length > 0) {
                                                  const minCount = Math.min(
                                                    ...countList,
                                                  );
                                                  const maxCount = Math.max(
                                                    ...countList,
                                                  );
                                                  repsLabel =
                                                    minCount === maxCount
                                                      ? `${minCount} Counts`
                                                      : `${minCount}-${maxCount} Counts`;
                                                }
                                              }
                                              return `${numSets} Set${numSets !== 1 ? "s" : ""}${repsLabel ? ` • ${repsLabel}` : ""}`;
                                            })()}
                                          </span>
                                          {isWorkoutCompleted &&
                                            workout.completedAt && (
                                              <span className="text-[9px] text-muted-foreground font-mono font-medium mt-0.5">
                                                {formatCompletionText(
                                                  workout.completedBy,
                                                  workout.completedByName,
                                                  workout.completedAt,
                                                )}
                                              </span>
                                            )}
                                        </div>
                                      </div>
                                    </div>

                                    <div
                                      className="flex items-center gap-2"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <Badge
                                        variant="outline"
                                        className="text-[8px] font-mono uppercase px-1 py-0"
                                      >
                                        {workout.workoutMetric || "reps"}
                                      </Badge>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setExpandedWorkoutId((prev) => {
                                            const wasExpanded =
                                              prev === workoutKey;
                                            if (!wasExpanded) {
                                              setTimeout(() => {
                                                const el =
                                                  document.getElementById(
                                                    `workout-container-${workoutKey}`,
                                                  );
                                                el?.scrollIntoView({
                                                  behavior: "smooth",
                                                  block: "start",
                                                });
                                              }, 150);
                                              return workoutKey;
                                            } else {
                                              return null;
                                            }
                                          });
                                        }}
                                        className="p-1 hover:bg-secondary rounded text-muted-foreground transition-colors"
                                      >
                                        <ChevronDown
                                          className={cn(
                                            "h-3.5 w-3.5 transition-transform duration-200",
                                            expandedWorkoutId === workoutKey &&
                                            "rotate-180",
                                          )}
                                        />
                                      </button>
                                    </div>
                                  </div>
                                </div>

                                {/* Workout Content */}
                                {expandedWorkoutId === workoutKey && (
                                  <div className="p-2 space-y-2 border-t border-border/40 bg-secondary/10 rounded-b-xl">
                                    {/* Instructions */}
                                    {(workout.description ||
                                      workout.instructions) && (
                                        <div className="text-[10.5px] leading-relaxed border-l-2 border-amber-500 bg-amber-500/5 dark:bg-amber-500/10 p-2.5 rounded-r-lg my-2 font-medium text-foreground/90 flex items-start gap-2 shadow-sm">
                                          <AlertCircle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                                          <span
                                            dangerouslySetInnerHTML={{
                                              __html:
                                                workout.description ||
                                                workout.instructions,
                                            }}
                                          />
                                        </div>
                                      )}

                                    {/* Subtask Sets Checklist */}
                                    {loggedSets.length > 0 && (
                                      <div className="space-y-2">
                                        <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1">
                                          <span>
                                            Total Sets ({loggedSets.length})
                                          </span>
                                          {isCompletionAllowed && (
                                            <>
                                              <span className="text-muted-foreground/30 font-normal">
                                                |
                                              </span>
                                              <span className="text-primary/70">
                                                Customize the values
                                              </span>
                                            </>
                                          )}
                                        </p>

                                        <div className="space-y-2">
                                          {loggedSets.map((set, sIdx) => {
                                            let suggestText = "";
                                            if (
                                              workout.workoutMetric === "reps"
                                            ) {
                                              suggestText = `${set.reps || 10} reps ${set.weightKg ? `@${set.weightKg} kg` : ""}`;
                                            } else if (
                                              workout.workoutMetric ===
                                              "duration"
                                            ) {
                                              suggestText = `${set.durationMin || 1} mins`;
                                            } else if (
                                              workout.workoutMetric ===
                                              "distance"
                                            ) {
                                              suggestText = `${set.distanceKm || 1} kms`;
                                            } else if (
                                              workout.workoutMetric === "count"
                                            ) {
                                              suggestText = `${set.count || 10} counts`;
                                            }

                                            const isSetChecked =
                                              !!set.completed;
                                            const isInputDisabled =
                                              !isCompletionAllowed ||
                                              isSetChecked ||
                                              localSession.status ===
                                              "completed";

                                            return (
                                              <div
                                                key={sIdx}
                                                className={cn(
                                                  "flex flex-wrap items-center justify-between gap-1.5 p-1 px-1.5 rounded-lg border text-[11px] select-none transition-all duration-200",
                                                  isSetChecked
                                                    ? "bg-emerald-500/5 border-emerald-500/20 text-foreground"
                                                    : "bg-secondary/100 border-border/40 text-muted-foreground",
                                                )}
                                              >
                                                <div
                                                  className="flex items-center gap-1.5 cursor-pointer py-0.5"
                                                  onClick={() => {
                                                    if (
                                                      isCompletionAllowed &&
                                                      secIdxInFull !== -1
                                                    ) {
                                                      handleToggleSetComplete(
                                                        secIdxInFull,
                                                        wIdx,
                                                        sIdx,
                                                        !isSetChecked,
                                                      );
                                                    }
                                                  }}
                                                >
                                                  {isCompletionAllowed && (
                                                    <Checkbox
                                                      checked={isSetChecked}
                                                      onCheckedChange={(
                                                        checked,
                                                      ) => {
                                                        if (
                                                          secIdxInFull !== -1
                                                        ) {
                                                          handleToggleSetComplete(
                                                            secIdxInFull,
                                                            wIdx,
                                                            sIdx,
                                                            !!checked,
                                                          );
                                                        }
                                                      }}
                                                      onClick={(e) =>
                                                        e.stopPropagation()
                                                      }
                                                      className="h-5 w-5 text-emerald-500 border-muted-foreground/30 rounded-md pointer-events-none bg-card"
                                                    />
                                                  )}
                                                  <span className="font-mono text-[11px] font-bold text-foreground">
                                                    Set {sIdx + 1}
                                                  </span>
                                                  {suggestText && (
                                                    <span className="text-10text-muted-foreground">
                                                      ({suggestText})
                                                    </span>
                                                  )}
                                                  {isSetChecked &&
                                                    set.completedAt && (
                                                      <span
                                                        className="text-[9.5px] text-emerald-600/80 dark:text-emerald-400/80 font-mono font-medium ml-1.5 shrink-0"
                                                        title={formatCompletionText(
                                                          set.completedBy,
                                                          set.completedByName,
                                                          set.completedAt,
                                                        )}
                                                      >
                                                        ✓{" "}
                                                        {formatCompletionTimeText(
                                                          set.completedBy,
                                                          set.completedByName,
                                                          set.completedAt,
                                                        )}
                                                      </span>
                                                    )}
                                                  {isCompletionAllowed &&
                                                    enableTimer &&
                                                    secIdxInFull !== -1 && (
                                                      <div
                                                        className="flex items-center gap-1.5 ml-1.5"
                                                        onClick={(e) =>
                                                          e.stopPropagation()
                                                        }
                                                      >
                                                        {activeTimer &&
                                                          activeTimer.secIdx ===
                                                          secIdxInFull &&
                                                          activeTimer.wIdx ===
                                                          wIdx &&
                                                          activeTimer.setIdx ===
                                                          sIdx ? (
                                                          <div className="flex items-center gap-1.5">
                                                            <span className="text-orange-500 font-mono text-[9.5px] font-bold animate-pulse bg-orange-500/10 px-1 py-0.5 rounded border border-orange-500/20">
                                                              {formatTime(
                                                                elapsed,
                                                              )}
                                                            </span>
                                                            <button
                                                              onClick={() =>
                                                                handleStopTimerAndComplete(
                                                                  secIdxInFull,
                                                                  wIdx,
                                                                  sIdx,
                                                                )
                                                              }
                                                              className="px-1.5 py-0.5 rounded bg-red-500 hover:bg-red-600 text-white text-[9px] font-medium flex items-center gap-0.5 transition-colors border-0 shrink-0 cursor-pointer"
                                                            >
                                                              <Square className="h-2 w-2 fill-white text-white" />
                                                              Stop & Complete
                                                            </button>
                                                          </div>
                                                        ) : (
                                                          !isSetChecked && (
                                                            <button
                                                              onClick={() =>
                                                                handleStartTimer(
                                                                  secIdxInFull,
                                                                  wIdx,
                                                                  sIdx,
                                                                )
                                                              }
                                                              className="px-1.5 py-0.5 rounded bg-primary hover:bg-primary/90 text-primary-foreground text-[9px] font-medium flex items-center gap-0.5 transition-colors border-0 shrink-0 cursor-pointer"
                                                            >
                                                              <Play className="h-2 w-2 fill-current" />
                                                              Start
                                                            </button>
                                                          )
                                                        )}
                                                      </div>
                                                    )}
                                                </div>

                                                <div className="flex items-center gap-1 text-[10px]">
                                                  {workout.workoutMetric ===
                                                    "reps" && (
                                                      <>
                                                        <div className="flex items-center gap-0.5 w-full">
                                                          <input
                                                            type="number"
                                                            inputMode="numeric"
                                                            pattern="[0-9]*"
                                                            disabled={
                                                              isInputDisabled
                                                            }
                                                            value={
                                                              set.reps !==
                                                                undefined
                                                                ? set.reps
                                                                : ""
                                                            }
                                                            onChange={(e) => {
                                                              const val =
                                                                e.target.value;
                                                              if (
                                                                secIdxInFull !==
                                                                -1
                                                              ) {
                                                                handleUpdateSetMetric(
                                                                  secIdxInFull,
                                                                  wIdx,
                                                                  sIdx,
                                                                  "reps",
                                                                  val === ""
                                                                    ? ""
                                                                    : parseInt(
                                                                      val,
                                                                    ) || 0,
                                                                );
                                                              }
                                                            }}
                                                            className="w-[60px] h-7 text-center bg-card border border-border/80 rounded font-mono p-0.5 text-xs text-foreground disabled:opacity-40"
                                                          />
                                                          <span className="text-[9.5px] hidden max-[375px]:block">
                                                            r
                                                          </span>
                                                          <span className="text-[9.5px] max-[375px]:hidden">
                                                            reps
                                                          </span>
                                                        </div>
                                                        <span>@</span>
                                                        <div className="flex items-center gap-0.5">
                                                          <input
                                                            type="number"
                                                            inputMode="decimal"
                                                            disabled={
                                                              isInputDisabled
                                                            }
                                                            value={
                                                              set.weightKg !==
                                                                undefined
                                                                ? set.weightKg
                                                                : ""
                                                            }
                                                            onChange={(e) => {
                                                              const val =
                                                                e.target.value;
                                                              if (
                                                                secIdxInFull !==
                                                                -1
                                                              ) {
                                                                handleUpdateSetMetric(
                                                                  secIdxInFull,
                                                                  wIdx,
                                                                  sIdx,
                                                                  "weightKg",
                                                                  val === ""
                                                                    ? ""
                                                                    : parseFloat(
                                                                      val,
                                                                    ) || 0,
                                                                );
                                                              }
                                                            }}
                                                            className="w-[60px] h-7 text-center bg-card border border-border/80 rounded font-mono p-0.5 text-xs text-foreground disabled:opacity-40"
                                                          />
                                                          <span className="text-[9.5px]">
                                                            kg
                                                          </span>
                                                        </div>
                                                      </>
                                                    )}

                                                  {workout.workoutMetric ===
                                                    "duration" && (
                                                      <div className="flex items-center gap-0.5">
                                                        <input
                                                          type="number"
                                                          inputMode="decimal"
                                                          disabled={
                                                            isInputDisabled
                                                          }
                                                          value={
                                                            set.durationMin !==
                                                              undefined
                                                              ? set.durationMin
                                                              : ""
                                                          }
                                                          onChange={(e) => {
                                                            const val =
                                                              e.target.value;
                                                            if (
                                                              secIdxInFull !== -1
                                                            ) {
                                                              handleUpdateSetMetric(
                                                                secIdxInFull,
                                                                wIdx,
                                                                sIdx,
                                                                "durationMin",
                                                                val === ""
                                                                  ? ""
                                                                  : parseFloat(
                                                                    val,
                                                                  ) || 0,
                                                              );
                                                            }
                                                          }}
                                                          className="w-[60px] h-7 text-center bg-card border border-border/80 rounded font-mono p-0.5 text-xs text-foreground disabled:opacity-40"
                                                        />
                                                        <span className="text-[9.5px]">
                                                          mins
                                                        </span>
                                                      </div>
                                                    )}

                                                  {workout.workoutMetric ===
                                                    "distance" && (
                                                      <div className="flex items-center gap-0.5">
                                                        <input
                                                          type="number"
                                                          inputMode="decimal"
                                                          disabled={
                                                            isInputDisabled
                                                          }
                                                          value={
                                                            set.distanceKm !==
                                                              undefined
                                                              ? set.distanceKm
                                                              : ""
                                                          }
                                                          onChange={(e) => {
                                                            const val =
                                                              e.target.value;
                                                            if (
                                                              secIdxInFull !== -1
                                                            ) {
                                                              handleUpdateSetMetric(
                                                                secIdxInFull,
                                                                wIdx,
                                                                sIdx,
                                                                "distanceKm",
                                                                val === ""
                                                                  ? ""
                                                                  : parseFloat(
                                                                    val,
                                                                  ) || 0,
                                                              );
                                                            }
                                                          }}
                                                          className="w-[60px] h-7 text-center bg-card border border-border/80 rounded font-mono p-0.5 text-xs text-foreground disabled:opacity-40"
                                                        />
                                                        <span className="text-[9.5px]">
                                                          km
                                                        </span>
                                                      </div>
                                                    )}

                                                  {workout.workoutMetric ===
                                                    "count" && (
                                                      <div className="flex items-center gap-0.5">
                                                        <input
                                                          type="number"
                                                          inputMode="numeric"
                                                          pattern="[0-9]*"
                                                          disabled={
                                                            isInputDisabled
                                                          }
                                                          value={
                                                            set.count !==
                                                              undefined
                                                              ? set.count
                                                              : ""
                                                          }
                                                          onChange={(e) => {
                                                            const val =
                                                              e.target.value;
                                                            if (
                                                              secIdxInFull !== -1
                                                            ) {
                                                              handleUpdateSetMetric(
                                                                secIdxInFull,
                                                                wIdx,
                                                                sIdx,
                                                                "count",
                                                                val === ""
                                                                  ? ""
                                                                  : parseInt(
                                                                    val,
                                                                  ) || 0,
                                                              );
                                                            }
                                                          }}
                                                          className="w-[60px] h-7 text-center bg-card border border-border/80 rounded font-mono p-0.5 text-xs text-foreground disabled:opacity-40"
                                                        />
                                                        <span className="text-[9.5px] hidden max-[375px]:block">
                                                          cnt
                                                        </span>
                                                        <span className="text-[9.5px] max-[375px]:hidden">
                                                          counts
                                                        </span>
                                                      </div>
                                                    )}

                                                  {set.restSeconds &&
                                                    set.restSeconds !==
                                                    "none" &&
                                                    Number(set.restSeconds) >
                                                    0 && (
                                                      <div className="flex items-center gap-1 pl-1 border-l border-border/30">
                                                        <span className="text-[8.5px] text-amber-500 font-mono">
                                                          Rest:
                                                        </span>
                                                        <input
                                                          type="number"
                                                          inputMode="numeric"
                                                          pattern="[0-9]*"
                                                          disabled={
                                                            isInputDisabled
                                                          }
                                                          value={
                                                            set.restSeconds !==
                                                              undefined
                                                              ? set.restSeconds
                                                              : 0
                                                          }
                                                          onChange={(e) => {
                                                            const val =
                                                              e.target.value;
                                                            if (
                                                              secIdxInFull !==
                                                              -1
                                                            ) {
                                                              handleUpdateSetMetric(
                                                                secIdxInFull,
                                                                wIdx,
                                                                sIdx,
                                                                "restSeconds",
                                                                val === ""
                                                                  ? ""
                                                                  : parseInt(
                                                                    val,
                                                                  ) || 0,
                                                              );
                                                            }
                                                          }}
                                                          className="w-[50px] h-7 text-center bg-card border border-border/80 rounded font-mono p-0.5 text-xs text-foreground disabled:opacity-40"
                                                        />
                                                        <span className="text-[9.5px] hidden max-[375px]:block">
                                                          s
                                                        </span>
                                                        <span className="text-[9px] max-[375px]:hidden">
                                                          seconds
                                                        </span>
                                                      </div>
                                                    )}
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}

                                    {/* YouTube Link */}
                                    {workout.youtubeLink && (
                                      <div className="flex items-center gap-1.5 bg-red-500/5 hover:bg-red-500/10 transition-colors px-2.5 py-1 rounded border border-red-500/10 w-fit mt-1.5">
                                        <svg
                                          className="h-3 w-3 text-red-500 shrink-0"
                                          fill="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path d="M23.498 6.163a3.003 3.003 0 0 0-2.11-2.108C19.52 3.5 12 3.5 12 3.5s-7.52 0-9.388.555A3.002 3.002 0 0 0 .502 6.163C0 8.07 0 12 0 12s0 3.93 .502 5.837a3.003 3.003 0 0 0 2.11 2.108C4.48 20.5 12 20.5 12 20.5s7.52 0 9.388-.555a3.003 3.003 0 0 0 2.11-2.108C24 15.93 24 12 24 12s0-3.93-.502-5.837zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                                        </svg>
                                        <a
                                          href={workout.youtubeLink}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-[9.5px] text-red-600 dark:text-red-400 hover:underline flex items-center gap-0.5 font-semibold"
                                        >
                                          Video Tutorial{" "}
                                          <ExternalLink className="h-2.5 w-2.5 ml-0.5" />
                                        </a>
                                      </div>
                                    )}

                                    {/* Attachments */}
                                    {workout.attachments &&
                                      workout.attachments.length > 0 && (
                                        <div className="space-y-1 mt-1.5">
                                          <p className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">
                                            Attachments
                                          </p>
                                          <div className="flex flex-wrap gap-1.5">
                                            {workout.attachments.map(
                                              (file, fIdx) => {
                                                const fileUrl =
                                                  file.url.startsWith("http")
                                                    ? file.url
                                                    : `${BACKEND_URL}${file.url}`;
                                                const fileIsImage =
                                                  isImage(file);
                                                return fileIsImage ? (
                                                  <button
                                                    key={fIdx}
                                                    onClick={() => {
                                                      setPreviewDialog({
                                                        open: true,
                                                        src: fileUrl,
                                                        name:
                                                          file.name ||
                                                          "Image Attachment",
                                                      });
                                                    }}
                                                    className="flex items-center gap-1.5 text-[9px] bg-card hover:bg-secondary/40 border border-border px-2 py-0.5 rounded-md text-muted-foreground hover:text-foreground font-medium transition-colors cursor-pointer"
                                                  >
                                                    <span className="truncate max-w-[100px]">
                                                      {file.name ||
                                                        "Attachment"}
                                                    </span>
                                                  </button>
                                                ) : (
                                                  <a
                                                    key={fIdx}
                                                    href={fileUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center gap-1.5 text-[9px] bg-card hover:bg-secondary/40 border border-border px-2 py-0.5 rounded-md text-muted-foreground hover:text-foreground font-medium transition-colors"
                                                  >
                                                    <span className="truncate max-w-[100px]">
                                                      {file.name ||
                                                        "Attachment"}
                                                    </span>
                                                  </a>
                                                );
                                              },
                                            )}
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                )}
                              </div>
                            );
                          },
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-6">
              No workouts listed for this day.
            </p>
          )}

          {/* {hasWorkouts &&
            ["upcoming", "missed", "skipped"].includes(localSession.status) &&
            isCompletionAllowed && (
              <button
                onClick={handleMarkCompleteBulk}
                disabled={loading}
                className="w-full py-1.5 flex items-center justify-center gap-1.5 rounded-lg bg-green-500/10 hover:bg-green-500/20 text-green-600 dark:text-green-400 disabled:opacity-50 text-xs font-bold transition-colors mt-4 mb-2 border-0"
              >
                {loading ? (
                  "Updating..."
                ) : (
                  <>
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Mark all as Completed
                  </>
                )}
              </button>
            )}

          {hasWorkouts &&
            localSession.status === "completed" &&
            isCompletionAllowed && (
              <button
                onClick={handleMarkUncompleteBulk}
                disabled={loading}
                className="w-full py-1.5 flex items-center justify-center gap-1.5 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-600 dark:text-amber-400 disabled:opacity-50 text-xs font-bold transition-colors mt-2 mb-2 border-0"
              >
                {loading ? (
                  "Updating..."
                ) : (
                  <>
                    <XCircle className="h-3.5 w-3.5" />
                    Mark as Uncompleted
                  </>
                )}
              </button>
            )} */}
        </CardContent>
      </Card>

      <Dialog
        open={!!confirmDialog}
        onOpenChange={(open) => {
          if (!open && !confirmSubmitting) setConfirmDialog(null);
        }}
      >
        <DialogContent className="max-w-sm bg-card border-border p-5 rounded-lg flex flex-col gap-4">
          <DialogHeader className="text-left space-y-1.5 shrink-0">
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold font-heading uppercase tracking-wide">
              {confirmDialog?.title}
            </DialogTitle>
            <div className="text-[11.5px] text-muted-foreground leading-relaxed flex flex-col gap-2">
              {confirmDialog?.type === "timer" && (
                <div className="flex flex-col gap-2">
                  <p>Awesome effort! You finished your set.</p>
                  <div className="my-1 p-2.5 bg-secondary/35 rounded-lg border border-border/40 flex items-center justify-between">
                    <span className="font-semibold text-foreground text-xs">
                      {confirmDialog.workoutTitle}
                    </span>
                    <span className="text-orange-500 font-mono text-[10px] font-bold bg-orange-500/10 px-1.5 py-0.5 rounded border border-orange-500/20">
                      {confirmDialog.formattedTime}
                    </span>
                  </div>
                  <p className="font-medium text-foreground">
                    Would you like to mark this set as completed?
                  </p>
                </div>
              )}
              {confirmDialog?.type === "set" && (
                <div className="flex flex-col gap-2">
                  <p>Are you sure you want to update this set's status?</p>
                  <div className="my-1 p-2.5 bg-secondary/35 rounded-lg border border-border/40 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[9.5px] font-bold bg-primary/15 text-primary px-1.5 py-0.5 rounded border border-primary/25">
                        Set {confirmDialog.setNumber}
                      </span>
                      <span className="font-semibold text-foreground text-xs">
                        {confirmDialog.workoutTitle}
                      </span>
                    </div>
                    <span
                      className={cn(
                        "text-[9px] uppercase tracking-wider font-bold",
                        confirmDialog.action === "complete"
                          ? "text-emerald-500"
                          : "text-amber-500",
                      )}
                    >
                      {confirmDialog.action === "complete"
                        ? "Completed"
                        : "Reset"}
                    </span>
                  </div>
                </div>
              )}
              {confirmDialog?.type === "workout" && (
                <div className="flex flex-col gap-2">
                  <p>Are you sure you want to update this exercise's status?</p>
                  <div className="my-1 p-2.5 bg-secondary/35 rounded-lg border border-border/40 flex items-center justify-between">
                    <span className="font-semibold text-foreground text-xs">
                      {confirmDialog.workoutTitle}
                    </span>
                    <span
                      className={cn(
                        "text-[9px] uppercase tracking-wider font-bold",
                        confirmDialog.action === "complete"
                          ? "text-emerald-500"
                          : "text-amber-500",
                      )}
                    >
                      {confirmDialog.action === "complete"
                        ? "Completed"
                        : "Reset"}
                    </span>
                  </div>
                </div>
              )}
              {confirmDialog?.type === "section" && (
                <div className="flex flex-col gap-2">
                  <p>Are you sure you want to update this section's status?</p>
                  <div className="my-1 p-2.5 bg-secondary/35 rounded-lg border border-border/40 flex items-center justify-between">
                    <span className="font-semibold text-foreground text-xs">
                      {confirmDialog.sectionName}
                    </span>
                    <span
                      className={cn(
                        "text-[9px] uppercase tracking-wider font-bold",
                        confirmDialog.action === "complete"
                          ? "text-emerald-500"
                          : "text-amber-500",
                      )}
                    >
                      {confirmDialog.action === "complete"
                        ? "Completed"
                        : "Reset"}
                    </span>
                  </div>
                </div>
              )}
              {(confirmDialog?.type === "session-auto" ||
                confirmDialog?.type === "session-bulk") && (
                  <div className="flex flex-col gap-2">
                    <div className="whitespace-pre-line">
                      {confirmDialog.description}
                    </div>
                  </div>
                )}
              {!confirmDialog?.type && confirmDialog?.description}
            </div>
          </DialogHeader>
          <DialogFooter className="flex items-center flex-row justify-end gap-2 shrink-0">
            <button
              disabled={confirmSubmitting}
              onClick={() => setConfirmDialog(null)}
              className="px-4 py-1.5 text-xs font-mono font-heading  uppercase tracking-wider bg-secondary hover:bg-secondary/80 text-foreground rounded-lg transition-colors border-0 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              disabled={confirmSubmitting}
              onClick={async () => {
                setConfirmSubmitting(true);
                try {
                  await confirmDialog?.onConfirm?.();
                  setConfirmDialog(null);
                } catch (err) {
                  console.error(err);
                } finally {
                  setConfirmSubmitting(false);
                }
              }}
              className={cn(
                "px-4 py-1.5 text-xs font-mono font-heading uppercase tracking-wider rounded-lg transition-colors border-0 flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed min-w-[70px]",
                confirmDialog?.action === "complete"
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                  : confirmDialog?.action === "reset"
                    ? "bg-amber-600 hover:bg-amber-700 text-white"
                    : "bg-primary hover:bg-primary/90 text-primary-foreground",
              )}
            >
              {confirmSubmitting && (
                <Loader2
                  className={cn(
                    "h-3 w-3 animate-spin",
                    confirmDialog?.action === "complete" ||
                      confirmDialog?.action === "reset"
                      ? "text-white"
                      : "text-primary-foreground",
                  )}
                />
              )}
              {confirmDialog?.action === "complete"
                ? "Mark as Complete"
                : confirmDialog?.action === "reset"
                  ? "Mark as Uncomplete"
                  : "Confirm"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={previewDialog.open}
        onOpenChange={(o) => setPreviewDialog((d) => ({ ...d, open: o }))}
      >
        <DialogContent className="bg-card border-border max-w-sm p-4 rounded-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-base font-black text-foreground uppercase tracking-wide">
              {previewDialog.name}
            </DialogTitle>
          </DialogHeader>
          <img
            src={previewDialog.src}
            alt={previewDialog.name}
            className="rounded-sm max-h-80 w-full object-contain border border-border mt-2"
          />
        </DialogContent>
      </Dialog>

      {showFlexibleDialog && (
        <Dialog open={showFlexibleDialog} onOpenChange={setShowFlexibleDialog}>
          <DialogContent className="sm:max-w-[480px]">
            <DialogHeader className="text-left">
              <DialogTitle className="font-heading font-semibold text-lg uppercase tracking-wide">
                Choose Workout Routine
              </DialogTitle>
              <DialogDescription className="text-xs">
                Select an assigned routine from the workout card to perform on
                this day.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
              {/* None (Clear Selection) option */}
              <div
                onClick={() => setTempSelected("none:none")}
                className={cn(
                  "p-3 rounded-xl border transition-all cursor-pointer flex flex-col gap-2",
                  tempSelected === "none:none"
                    ? "bg-primary/5 border-primary shadow-sm"
                    : "bg-card border-border hover:bg-secondary/40 text-foreground",
                )}
              >
                <div className="flex items-center gap-2">
                  <div
                    className={cn(
                      "h-4 w-4 rounded-full border flex items-center justify-center shrink-0",
                      tempSelected === "none:none"
                        ? "border-primary"
                        : "border-muted-foreground/45",
                    )}
                  >
                    {tempSelected === "none:none" && (
                      <div className="h-2 w-2 rounded-full bg-primary" />
                    )}
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wide">
                    None (Clear Selection)
                  </span>
                </div>
                <p className="text-[10px] text-muted-foreground border-t border-border/20 pt-1">
                  Clear any selected workouts for this day.
                </p>
              </div>

              {flexibleOptions.map((opt) => {
                const isSelected = tempSelected === opt.value;

                return (
                  <div
                    key={opt.value}
                    onClick={() => setTempSelected(opt.value)}
                    className={cn(
                      "p-3 rounded-xl border transition-all cursor-pointer flex flex-col gap-2",
                      isSelected
                        ? "bg-primary/5 border-primary shadow-sm"
                        : "bg-card border-border hover:bg-secondary/40 text-foreground",
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className={cn(
                          "h-4 w-4 rounded-full border flex items-center justify-center shrink-0",
                          isSelected
                            ? "border-primary"
                            : "border-muted-foreground/45",
                        )}
                      >
                        {isSelected && (
                          <div className="h-2 w-2 rounded-full bg-primary" />
                        )}
                      </div>
                      <span className="text-xs font-bold uppercase tracking-wide">
                        {opt.label}
                      </span>
                    </div>

                    {opt.sections &&
                      opt.sections.some(
                        (sec) =>
                          Array.isArray(sec.workouts) && sec.workouts.length > 0,
                      ) ? (
                      <div className="flex flex-wrap gap-x-2 gap-y-1 border-t border-border/20 pt-1.5">
                        {opt.sections.map((sec, sIdx) => {
                          const wCount = Array.isArray(sec.workouts)
                            ? sec.workouts.length
                            : 0;
                          if (wCount === 0) return null;
                          return (
                            <span
                              key={sIdx}
                              className="text-[10px] text-muted-foreground bg-secondary/85 px-1.5 py-0.5 rounded flex items-center gap-1"
                            >
                              <span className="text-[8px] font-black text-primary/70 uppercase tracking-wide mr-0.5">
                                {sec.label}:
                              </span>
                              <span className="truncate" title={sec.name}>
                                {sec.name}
                              </span>
                              <span className="font-bold font-mono">
                                ({wCount})
                              </span>
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-[10px] text-muted-foreground/60 italic border-t border-border/20 pt-1.5">
                        Rest / Recovery
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <DialogFooter className="gap-2 sm:gap-0 mt-2 flex flex-row justify-end">
              <button
                type="button"
                onClick={() => setShowFlexibleDialog(false)}
                className="border border-border bg-card px-4 py-2 text-xs font-bold font-heading uppercase tracking-wide rounded-lg hover:bg-secondary/40 text-foreground transition-all mr-2"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmFlexibleSelection}
                disabled={loading || !tempSelected}
                className="bg-primary text-primary-foreground px-4 py-2 text-xs font-bold font-heading uppercase tracking-wide rounded-lg hover:bg-primary/95 transition-all disabled:opacity-50"
              >
                {loading ? "Saving..." : "Confirm Selection"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
